import {
  Input,
  Card,
  Button,
  Modal,
  NavLink,
  Checkbox,
  Select,
  Switch,
  Menu,
  Tabs,
  Divider,
  Anchor,
  TextInput,
  Combobox,
  Avatar,
  ActionIcon,
  Slider,
  RangeSlider,
  type MantineThemeComponents,
  PasswordInput,
  NumberInput,
} from "@mantine/core";
import { clsx } from "clsx";
import { BLACK_IN_LIGHT_MODE } from "@mapka/style-common";
import { AlertOverwrite } from "./alert.js";
import { AccordionOverwrite } from "./accordion.js";
import { NotificationOverwrite } from "./notification.js";
import { BadgeOverwrite } from "./badge.js";

// Extend Mantine's Button variant types with custom variants
declare module "@mantine/core" {
  export interface ButtonProps {
    variant?:
      | "filled"
      | "outline"
      | "light"
      | "subtle"
      | "default"
      | "transparent"
      | "white"
      // Custom Mapka variants
      | "primary"
      | "secondary"
      | "tertiary"
      | "text"
      | "danger";
  }

  export interface CardProps {
    variant?: "shadowless";
  }
}

export const components = {
  Button: Button.extend({
    classNames: {
      root: "font-mono mapka-button",
    },
    defaultProps: {
      color: "black",
    },
  }),

  NavLink: NavLink.extend({
    classNames: {
      root: "mapka-nav-link",
      label: "mapka-nav-link-label",
      section: "mapka-nav-link-section",
    },
  }),

  Anchor: Anchor.extend({
    classNames: {
      root: "mapka-anchor",
    },
  }),

  ActionIcon: ActionIcon.extend({
    defaultProps: {
      color: "black",
    },
    classNames: {
      root: "mapka-action-icon",
    },
  }),

  Divider: Divider.extend({
    classNames: {
      root: "mapka-divider",
    },
    vars: () => ({
      root: {
        "--divider-color": BLACK_IN_LIGHT_MODE,
      },
    }),
  }),

  Card: Card.extend({
    classNames: (_, props) => ({
      root: clsx("mapka-card", {
        "mapka-card-shadowless": props.variant === "shadowless",
      }),
    }),
  }),

  Input: Input.extend({
    classNames: {
      input: "mapka-input",
      wrapper: "mapka-input-wrapper",
    },
  }),

  TextInput: TextInput.extend({
    classNames: {
      label: "mapka-input-label semibold",
    },
  }),

  PasswordInput: PasswordInput.extend({
    classNames: {
      label: "mapka-input-label",
    },
  }),

  NumberInput: NumberInput.extend({
    classNames: {
      label: "mapka-input-label",
    },
  }),

  Checkbox: Checkbox.extend({
    classNames: {
      input: "mapka-input mapka-checkbox",
      label: "mapka-checkbox-label",
    },
  }),

  Select: Select.extend({
    classNames: {
      dropdown: "mapka-select-dropdown",
    },
  }),

  Combobox: Combobox.extend({
    classNames: {
      dropdown: "mapka-select-dropdown",
    },
  }),

  Menu: Menu.extend({
    classNames: {
      dropdown: "mapka-menu-dropdown",
      item: "mapka-menu-item",
    },
  }),

  Switch: Switch.extend({
    classNames: {
      track: "mapka-switch-track",
      thumb: "mapka-switch-thumb",
    },
  }),

  Tabs: Tabs.extend({
    classNames: {
      root: "mapka-tabs",
      tab: "mapka-tabs-tab",
      list: "mapka-tabs-list",
    },
  }),

  Slider: Slider.extend({
    classNames: {
      track: "mapka-slider-track",
      bar: "mapka-slider-bar",
      thumb: "mapka-slider-thumb",
      label: "mapka-slider-label",
      markLabel: "mapka-slider-markLabel",
    },
    vars: () => ({
      root: {
        "--slider-color": BLACK_IN_LIGHT_MODE,
        "--slider-thumb-size": "16px",
        "--slider-radius": "8px",
      },
    }),
  }),

  RangeSlider: RangeSlider.extend({
    classNames: {
      track: "mapka-slider-track",
      bar: "mapka-slider-bar",
      thumb: "mapka-slider-thumb",
      label: "mapka-slider-label",
      markLabel: "mapka-slider-markLabel",
    },
    vars: () => ({
      root: {
        "--slider-color": BLACK_IN_LIGHT_MODE,
        "--slider-thumb-size": "16px",
        "--slider-radius": "8px",
      },
    }),
  }),

  Modal: Modal.extend({
    classNames: {
      content: "mapka-modal",
    },
  }),

  Avatar: Avatar.extend({
    classNames: {
      root: "mapka-avatar",
    },
  }),

  Notification: NotificationOverwrite,
  Accordion: AccordionOverwrite,
  Alert: AlertOverwrite,
  Badge: BadgeOverwrite,
} satisfies MantineThemeComponents;
